/**
 * Shared constants for the experimental v2 object schema.
 * Import from here — never duplicate these across object files.
 */

/**
 * FontResource naming convention
 *
 * - Primary font slot:
 *       font
 *
 * - Additional independent slots:
 *       <role>_font_resource
 *
 * The _resource suffix is only used when the corresponding
 * <role>_font property already exists as the numeric font size.
 *
 * Examples:
 *
 *   font
 *   value_font_resource
 *   item_font_resource
 *   title_font_resource
 *   scale_font_resource
 */

// OpenHASP wire-format serialization metadata ONLY — this is not an LVGL
// concept. It maps a semantic part name to the numeric suffix the OpenHASP
// firmware JSON protocol appends to a property name for that part
// (bg_color + '10' → bg_color10). It exists solely as a fallback for object
// schemas whose parts don't yet declare their own `exportSuffix` (see part
// metadata below). Do not add entries for newly migrated widgets — declare
// `exportSuffix` in the schema itself instead. Existing keys must not be
// renamed: several schemas (gauge, roller, btnmatrix, msgbox, tabview, etc.)
// still resolve their wire suffix through this map by part name. Consumed
// exclusively by wireFormat.js (the OpenHASP import/export adapter) and by
// schemaValidator.js (which validates the wire-mapping metadata itself) —
// no other code may reference it.
export const OPENHASP_PART_SUFFIX = {
    main:          0,
    indicator:    10,
    knob:         20,
    items:        30,
    item_buttons: 40,
    selected:     50,
    major_ticks:  60,
    text_cursor:  70,
    scrollbar:    80,
    special:      90,
}

// Firmware press lifecycle shared by the widgets that use the generic touch
// event handler. `click` is emitted for a short press (down -> up without a
// long event).
export const PRESS_EVENTS = {
    down: {},
    up: {},
    click: {},
    long: {},
    hold: {},
    release: {},
}

// Event definitions mirrored from the firmware event handlers. Keep these
// reusable so widget schemas cannot drift apart when a firmware event is
// added or removed.
export const TOGGLE_EVENTS = {
    down: {
        payload: ['val'],
        payloadMetadata: { val: { type: 'int', label: 'Value' } },
    },
    up: {
        payload: ['val'],
        payloadMetadata: { val: { type: 'int', label: 'Value' } },
    },
    click: { payload: [], payloadMetadata: {} },
    changed: {
        payload: ['val'],
        payloadMetadata: { val: { type: 'int', label: 'Value' } },
    },
}

// A btn with `toggle: true` runs the firmware toggle handler, which also
// reports long/hold with the checked value (and no release event).
export const TOGGLE_BUTTON_EVENTS = {
    ...TOGGLE_EVENTS,
    long: {
        payload: ['val'],
        payloadMetadata: { val: { type: 'int', label: 'Value' } },
    },
    hold: {
        payload: ['val'],
        payloadMetadata: { val: { type: 'int', label: 'Value' } },
    },
}

// Swipe gestures (hasp_event.cpp swipe_event_handler): LV_EVENT_GESTURE runs
// the object's `swipe` commands on the device itself. Gestures are not
// published over MQTT, so they are kept out of `events` (which also feeds the
// Home Assistant triggers) and are offered by the logic editor only.
export const SWIPE_GESTURES = {
    swipe_left:  { label: 'Swipe left',  wire: { attribute: 'swipe', key: 'left' } },
    swipe_right: { label: 'Swipe right', wire: { attribute: 'swipe', key: 'right' } },
    swipe_up:    { label: 'Swipe up',    wire: { attribute: 'swipe', key: 'up' } },
    swipe_down:  { label: 'Swipe down',  wire: { attribute: 'swipe', key: 'down' } },
}

export const SELECTION_EVENTS = {
    down: {
        payload: ['val', 'text'],
        payloadMetadata: {
            val: { type: 'int', label: 'Selected index' },
            text: { type: 'string', label: 'Selected text' },
        },
    },
    up: {
        payload: ['val', 'text'],
        payloadMetadata: {
            val: { type: 'int', label: 'Selected index' },
            text: { type: 'string', label: 'Selected text' },
        },
    },
    click: { payload: [], payloadMetadata: {} },
    changed: {
        payload: ['val', 'text'],
        payloadMetadata: {
            val: { type: 'int', label: 'Selected index' },
            text: { type: 'string', label: 'Selected text' },
        },
    },
}

// Button matrices publish the selected button payload for every gesture event.
// Keep this separate from SELECTION_EVENTS: dropdown/roller/tabview currently
// only expose their firmware-backed selection events, while btnmatrix also
// exposes the complete press lifecycle.
export const BUTTON_MATRIX_EVENTS = {
    down: {
        payload: ['val', 'text'],
        payloadMetadata: {
            val: { type: 'int', label: 'Button index' },
            text: { type: 'string', label: 'Button text' },
        },
    },
    up: {
        payload: ['val', 'text'],
        payloadMetadata: {
            val: { type: 'int', label: 'Button index' },
            text: { type: 'string', label: 'Button text' },
        },
    },
    click: {
        payload: ['val', 'text'],
        payloadMetadata: {
            val: { type: 'int', label: 'Button index' },
            text: { type: 'string', label: 'Button text' },
        },
    },
    changed: {
        payload: ['val', 'text'],
        payloadMetadata: {
            val: { type: 'int', label: 'Button index' },
            text: { type: 'string', label: 'Button text' },
        },
    },
    long: {
        payload: ['val', 'text'],
        payloadMetadata: {
            val: { type: 'int', label: 'Button index' },
            text: { type: 'string', label: 'Button text' },
        },
    },
    hold: {
        payload: ['val', 'text'],
        payloadMetadata: {
            val: { type: 'int', label: 'Button index' },
            text: { type: 'string', label: 'Button text' },
        },
    },
    release: {
        payload: ['val', 'text'],
        payloadMetadata: {
            val: { type: 'int', label: 'Button index' },
            text: { type: 'string', label: 'Button text' },
        },
    },
}

export const VALUE_EVENTS = {
    // `rangePayload` describes the payload of the Range variant. Keeping this
    // on the event definition makes the action system independent of widget
    // names: any future control can opt into the same Range semantics.
    down: {
        rangePayload: ['start', 'val'],
        rangePayloadMetadata: {
            start: { type: 'int', label: 'Range start' },
            val: { type: 'int', label: 'Value' },
        },
    },
    up: {
        rangePayload: ['start', 'val'],
        rangePayloadMetadata: {
            start: { type: 'int', label: 'Range start' },
            val: { type: 'int', label: 'Value' },
        },
    },
    click: {
        rangePayload: ['start', 'val'],
        rangePayloadMetadata: {
            start: { type: 'int', label: 'Range start' },
            val: { type: 'int', label: 'Value' },
        },
    },
    changed: {
        payload: ['val'],
        payloadMetadata: { val: { type: 'int', label: 'Value' } },
        rangePayload: ['start', 'val'],
        rangePayloadMetadata: {
            start: { type: 'int', label: 'Range start' },
            val: { type: 'int', label: 'Value' },
        },
    },
}

// openHASP publishes the complete selected colour with the colour-picker
// gesture events. Keep this as typed event metadata so consumers can only
// suggest variables that the firmware actually sends for the selected event.
const COLOR_EVENT_PAYLOAD = ['color', 'r', 'g', 'b', 'h', 's', 'v']
const COLOR_EVENT_PAYLOAD_METADATA = {
    color: { type: 'color', label: 'Selected color' },
    r: { type: 'int', label: 'Red' },
    g: { type: 'int', label: 'Green' },
    b: { type: 'int', label: 'Blue' },
    h: { type: 'int', label: 'Hue' },
    s: { type: 'int', label: 'Saturation' },
    v: { type: 'int', label: 'Value' },
}

export const COLOR_EVENTS = {
    down: {
        payload: COLOR_EVENT_PAYLOAD,
        payloadMetadata: COLOR_EVENT_PAYLOAD_METADATA,
    },
    up: {
        payload: COLOR_EVENT_PAYLOAD,
        payloadMetadata: COLOR_EVENT_PAYLOAD_METADATA,
    },
    click: { payload: [], payloadMetadata: {} },
    changed: {
        payload: COLOR_EVENT_PAYLOAD,
        payloadMetadata: COLOR_EVENT_PAYLOAD_METADATA,
    },
}

// Home Assistant action-data metadata consumed by the EntityBuilder. Keeping
// this catalogue in schema-v2 means the editor does not need a second list of
// service parameters. Object schemas receive it through v2/index.js and may
// override or extend it when a widget needs a different action-data contract.
//
// `parameter` is the key written to an AutomationAction parameter and `value`
// is the Designer value inserted by the suggestion picker.
export const OBJECT_BACKGROUND_COLOR_REFERENCE = 'object.bg_color'

export const HA_ACTION_DATA_SUGGESTIONS = {
    openhasp: {
        command: [
            { parameter: 'keyword', value: 'jsonl',         label: 'keyword (jsonl)' },
            { parameter: 'parameters', value: '',            label: 'JSONL parameters (popup objects)' },
        ],
        delete: [
            { parameter: 'keyword', value: 'p0b200.delete', label: 'keyword (delete)' },
        ],
    },
    homeassistant: {
        toggle: [],
        turn_on: [],
        turn_off: [],
    },
    light: {
        turn_on: [
            { parameter: 'brightness',     value: '{{ val }}',                  label: 'Brightness (0-255)' },
            { parameter: 'brightness_pct', value: '{{ val }}',                  label: 'Brightness % (0-100)' },
            { parameter: 'color_temp',     value: '{{ val }}',                  label: 'Color temp (mireds)' },
            { parameter: 'kelvin',         value: '{{ val }}',                  label: 'Color temp (Kelvin)' },
            { parameter: 'rgb_color',      value: '[{{ r }},{{ g }},{{ b }}]', label: 'RGB color' },
            { parameter: 'hs_color',       value: '[{{ h }},{{ s }}]',          label: 'Hue / Saturation' },
            { parameter: 'white',          value: '{{ val }}',                  label: 'White channel' },
            { parameter: 'effect',         value: '{{ val }}',                  label: 'Effect name' },
            { parameter: 'transition',     value: '{{ val }}',                  label: 'Transition (s)' },
            { parameter: 'rgb_color',      value: OBJECT_BACKGROUND_COLOR_REFERENCE, label: 'Object background color (bg_color)' },
        ],
        turn_off: [
            { parameter: 'transition', value: '{{ val }}', label: 'Transition (s)' },
        ],
    },
    cover: {
        set_cover_position: [
            { parameter: 'position', value: '{{ val }}', label: 'Position (0-100)' },
        ],
        set_cover_tilt_position: [
            { parameter: 'tilt_position', value: '{{ val }}', label: 'Tilt position (0-100)' },
        ],
    },
    climate: {
        set_temperature: [
            { parameter: 'temperature', value: '{{ val | float / 10 }}', label: 'Temperature' },
            { parameter: 'hvac_mode', value: '{{ val }}',               label: 'HVAC mode' },
        ],
        set_hvac_mode: [
            { parameter: 'hvac_mode', value: '{{ val }}', label: 'HVAC mode' },
        ],
        set_fan_mode: [
            { parameter: 'fan_mode', value: '{{ val }}', label: 'Fan mode' },
        ],
        set_preset_mode: [
            { parameter: 'preset_mode', value: '{{ val }}', label: 'Preset mode' },
        ],
    },
    fan: {
        set_percentage: [
            { parameter: 'percentage', value: '{{ val }}', label: 'Percentage (0-100)' },
        ],
        set_preset_mode: [
            { parameter: 'preset_mode', value: '{{ val }}', label: 'Preset mode' },
        ],
    },
    media_player: {
        volume_set: [
            { parameter: 'volume_level', value: '{{ val | float / 100 }}', label: 'Volume (0.0 – 1.0)' },
        ],
        media_seek: [
            { parameter: 'seek_position', value: '{{ val }}', label: 'Seek position (s)' },
        ],
        volume_mute: [
            { parameter: 'is_volume_muted', value: 'false', label: 'Muted' },
        ],
        select_source: [
            { parameter: 'source', value: '{{ val }}', label: 'Source' },
        ],
        play_media: [
            { parameter: 'media_content_id', value: '{{ val }}', label: 'Media content ID' },
            { parameter: 'media_content_type', value: 'music', label: 'Media content type' },
        ],
    },
    input_number: {
        set_value: [
            { parameter: 'value', value: '{{ val }}', label: 'Value' },
        ],
    },
    input_select: {
        select_option: [
            { parameter: 'option', value: '{{ val }}', label: 'Option' },
        ],
    },
    humidifier: {
        set_humidity: [
            { parameter: 'humidity', value: '{{ val }}', label: 'Humidity %' },
        ],
    },
    number: {
        set_value: [
            { parameter: 'value', value: '{{ val }}', label: 'Value' },
        ],
    },
    select: {
        select_option: [
            { parameter: 'option', value: '{{ val }}', label: 'Option' },
        ],
    },
}

// An arc starts with the generic handler, but its adjustable attribute swaps
// in the slider handler. Its schema therefore exposes the union of both
// firmware lifecycles.
export const ARC_EVENTS = {
    down: VALUE_EVENTS.down,
    up: VALUE_EVENTS.up,
    click: VALUE_EVENTS.click,
    long: VALUE_EVENTS.down,
    hold: VALUE_EVENTS.down,
    release: VALUE_EVENTS.down,
    changed: VALUE_EVENTS.changed,
}

// Keep the old payload arrays enumerable and serializable. The richer typed
// metadata is available to schema consumers, but remains non-enumerable so
// existing schema snapshots and legacy equality checks keep their shape.
for (const eventSet of [TOGGLE_EVENTS, TOGGLE_BUTTON_EVENTS, SELECTION_EVENTS, VALUE_EVENTS, COLOR_EVENTS, ARC_EVENTS]) {
    for (const definition of Object.values(eventSet)) {
        for (const key of ['payloadMetadata', 'rangePayloadMetadata']) {
            if (!Object.hasOwn(definition, key)) continue
            Object.defineProperty(definition, key, {
                value: definition[key],
                enumerable: false,
                configurable: true,
            })
        }
    }
}

// The state half of the wire format used to live here, as OPENHASP_STATE_SUFFIX.
// It now lives with the rest of the state model in stateFormat.js, as a `digit`
// on each state, reachable through STATE_DIGITS and stateSuffix() — a state's
// name, label and wire digit belong together, and a second table here could
// only drift from the first.
//
// Note also that the part and state suffixes are SUMMED into one two-digit
// number rather than concatenated (indicator + checked is bg_color11, not
// bg_color1001), because that is how the firmware parses them back apart.
// See toWireKey() in wireFormat.js.

// ---------------------------------------------------------------------------
// Part metadata — modeled on LVGL 9's part system (lv_part_t: MAIN,
// SCROLLBAR, INDICATOR, KNOB, SELECTED, ITEMS, CURSOR, TICKS, CUSTOM_FIRST).
// Use the LVGL 9 part name whenever a widget's part has a real LVGL 9
// counterpart (main, indicator, knob, items, selected, scrollbar). Some part
// names here predate this alignment and don't map to a real LVGL 9 part
// (e.g. dropdown's `item_buttons`, an OpenHASP-specific styling hook for its
// list rows); keep those as-is rather than forcing an inexact rename — see
// v2/objects/dropdown.js for the reference implementation.
//
// Each entry in an object schema's `parts` map may declare, alongside
// `properties`:
//   label         – Accordion title shown in the Property Editor. Falls back
//                   to a generic default when omitted.
//   exportSuffix  – legacy firmware suffix appended to property names on
//                   export (e.g. 'bg_color' + '20' → 'bg_color20'). The
//                   single source of truth for wire-key generation once a
//                   schema declares it; schemas that omit it fall back to
//                   LVGL_PARTS above, keyed by part name.
//   order         – sort key controlling the part's position in the Property
//                   Editor relative to other parts of the same widget. Parts
//                   that omit it keep their declaration order (schemas that
//                   don't use `order` at all are unaffected).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Reusable style property sets for common parts.
// Each property carries full metadata used by PartPropertiesPanel for
// rendering and by wireFormat.js for export filtering.
//
// Metadata fields:
//   type           – 'color' | 'int' | 'bool' | 'enum' | 'string'
//   min / max      – numeric bounds (int only)
//   step           – increment step (int only)
//   unit           – display suffix, e.g. '°', 'ms'
//   values         – allowed values array (enum only)
//   options        – allowed values array for schema-driven string selectors
//   default        – editor fallback used by schema metadata consumers
//   format         – color picker format: 'hex' (default) | 'hex8' (has alpha)
//   hasp           – true → value stored with 'hasp_' prefix on the node
//   internal       – true → never exported to firmware, not a UI control
//   exportSpecial  – true → skipped in the main export loop; ObjectExport
//                    handles it via special per-property logic (e.g. padding
//                    expands to pad_top / pad_right / pad_left / pad_bottom)
//   exportAlways   – true → always emitted to firmware, even when the value
//                    comes from the widget default rather than an explicit
//                    per-instance override. The optimizer normally strips
//                    anything the cascade below already produces, which is
//                    right whenever the FIRMWARE reproduces that same default
//                    on its own. Use this for the cases where it does not, so
//                    the plate would otherwise render differently from the
//                    canvas — reference case: badge's main.radius, whose 999
//                    (circle) default has no counterpart in the firmware,
//                    where a badge is a plain lv_obj that would come out
//                    square. An explicit user value still wins: the resolved
//                    cascade value is what gets emitted, whatever produced it.
//   preserveExplicit – true → emit an explicit value even when it equals a
//                    lower-level schema/theme default. Unlike exportAlways,
//                    this never materializes an inherited value; it preserves
//                    only a value the design actually stored.
//   wireKey        – explicit override for this property's generated wire key,
//                    e.g. wireKey: 'x_scale'. Checked by wireFormat.js's
//                    toWireKey/fromWireKey BEFORE the part's exportSuffix/
//                    state-suffix derivation — when present, it wins outright
//                    and the property still flows through the normal single-
//                    value export/import path (computeEffectiveStyleEntries,
//                    fromWireFormatStates, ...), just under a different
//                    literal key. No other file needs to know about it.
//
//                    Use ONLY for a genuine 1:1 mapping between this property
//                    and a single firmware attribute whose real wire name
//                    doesn't follow the part it's grouped under for Property
//                    Editor/UX purposes — reference case: chart.js's
//                    `x_axis.x_scale` / `y_axis.y_scale`, which need to live
//                    under their own axis part for grouping, but are genuinely
//                    flat, unsuffixed firmware attributes (ATTR_X_SCALE/
//                    ATTR_Y_SCALE, hasp_chart.h) that don't take the axis
//                    part's exportSuffix.
//
//                    Do NOT use it for a property that changes SHAPE on
//                    export — expanding into multiple wire keys, combining
//                    several properties into one, or any transform beyond a
//                    literal pass-through value. `exportSpecial` remains the
//                    correct (and only) mechanism for that — e.g. padding's
//                    single value expanding into pad_top/pad_right/pad_left/
//                    pad_bottom, a 1-to-4 shape change no scalar rename can
//                    express. wireKey and exportSpecial solve different
//                    problems and are not interchangeable: wireKey renames a
//                    key that still holds one property's one value; a
//                    property should essentially never declare both.
//   label          – override display label when property name is unclear
//   defaultSource  – provenance of this property's `defaults.properties`/
//                    `defaults.styles` value. Omitted (or 'firmware') means
//                    the default is meant to reflect the actual OpenHASP/LVGL
//                    firmware default, and a firmware-parity audit finding a
//                    mismatch here is a real bug to fix. 'designer' means the
//                    default is a deliberate editor-authoring convention that
//                    intentionally differs from firmware's raw object-creation
//                    default — safe specifically when the widget's own export
//                    code always emits the property explicitly (never relies
//                    on firmware's default being consulted). Not read by any
//                    runtime code today; it exists so a firmware-parity audit
//                    can report "intentional divergence" instead of repeatedly
//                    re-flagging the same documented exception as a mismatch.
//                    Reference case: `line.js`'s `auto_size`/`y_invert` — see
//                    the property comments there for the full rationale.
// ---------------------------------------------------------------------------

// The main part's border properties, in one place. Spread by MAIN_STYLE and by
// the handful of widgets that still hand-roll their own `main` (checkbox,
// label, page, spinner) — so border_side/border_opa exist exactly once rather
// than being copied per widget. Rendered generically by the shared CustomRect
// (see src/draw/borderGeometry.ts); no widget has border code of its own.
//
// Main-only for now, deliberately: the shared renderer only draws `main`, so
// giving INDICATOR_STYLE/KNOB_STYLE a border_side would declare a property
// nothing honours. Those keep their own border_width/border_color pair.
export const BORDER_STYLE = {
    border_width: { type: 'int',    min: 0 },
    border_color: { type: 'color' },
    // LVGL opacity (0-255) for the border only — independent of bg_opa, exactly
    // as in firmware. Unset resolves to fully opaque (LV_OPA_COVER).
    border_opa:   { type: 'int',    min: 0, max: 255 },
    // LVGL border_side bitmask: 0=none, 1=bottom, 2=top, 4=left, 8=right,
    // 15=full; sums select combinations (7 = top+bottom+left). Stored and
    // exported as the raw numeric bitmask, identical to the wire value.
    // Unset resolves to full, matching lv_draw_rect_dsc_init().
    border_side:  { type: 'int',    min: 0, max: 15 },
}

// The main part's outline properties, in one place — same rationale as
// BORDER_STYLE above. Spread by MAIN_STYLE and by the hand-rolled `main` parts
// that expose a visible main object (checkbox, label, spinner), so the four
// properties exist exactly once. Rendered generically by the shared CustomRect
// (see src/draw/outlineGeometry.ts); no widget has outline code of its own.
//
// Main-only, like BORDER_STYLE: the shared renderer only draws `main`, so
// giving INDICATOR_STYLE/KNOB_STYLE an outline would declare properties nothing
// honours.
//
// Deliberately NOT spread into page/template_page: firmware accepts outline on
// a page generically, but a page fills the display, so its outline falls
// entirely outside the visible screen. Exposing it would add dead controls to
// the Page panel. A page remains the CLIPPING boundary for its children's
// outlines.
export const OUTLINE_STYLE = {
    outline_width: { type: 'int',    min: 0 },
    // hex8 like main's bg_color: the alpha channel IS outline_opa. The Property
    // Editor shows one picker with an alpha slider, and outline_opa below is
    // derived from it — there is no separate opacity field, exactly as for
    // bg_color/bg_opa (see ConfigManager's combine step and addSplitColors).
    outline_color: { type: 'color',  format: 'hex8' },
    // LVGL outline_opa (0-255) — independent of bg_opa, exactly as in firmware,
    // and fully opaque (LV_OPA_COVER) when unset. Internal: never edited or
    // exported directly; it is split back out of outline_color at export time.
    outline_opa:   { type: 'int',    min: 0, max: 255, internal: true },
    // Gap between the object's edge and the inside of the outline. Exported
    // verbatim as `outline_pad` (firmware's own attribute name — see
    // hasp_attribute.cpp attribute_outline_pad); unlike `padding` it needs no
    // per-side expansion, so it is NOT exportSpecial.
    outline_pad:   { type: 'int',    min: 0 },
}

// openHASP 0.7 background gradient properties. Values deliberately stay in
// the firmware's numeric representation throughout the model and wire path;
// the schema registry applies these properties wherever a part declares
// bg_color, so applicability follows the existing style schema rather than a
// second list of widgets/parts.
export const BG_GRADIENT_STYLE = {
    bg_grad_color: { type: 'color', label: 'Gradient color', preserveExplicit: true },
    bg_grad_dir: {
        type: 'enum',
        label: 'Gradient direction',
        values: [
            { value: 0, label: 'None', canvasAxis: 'none' },
            { value: 1, label: 'Vertical', canvasAxis: 'vertical' },
            { value: 2, label: 'Horizontal', canvasAxis: 'horizontal' },
        ],
        preserveExplicit: true,
    },
    bg_main_stop: {
        type: 'int', label: 'Gradient start', min: 0, max: 255,
        preserveExplicit: true,
    },
    bg_grad_stop: {
        type: 'int', label: 'Gradient stop', min: 0, max: 255,
        preserveExplicit: true,
    },
}

export const BACKGROUND_GROUP = {
    id: 'background',
    label: 'Background',
    // bg_opa remains internal and is edited through bg_color's existing hex8
    // picker alpha channel, then split back into the firmware bg_opa field.
    properties: ['bg_color', ...Object.keys(BG_GRADIENT_STYLE), 'bg_opa'],
}

// The main part's box-shadow properties, in one place — same rationale as
// BORDER_STYLE/OUTLINE_STYLE above. Rendered by src/draw/shadowGeometry.ts:
// shadow_width is the blur window, NOT a Gaussian radius; shadow_spread expands
// the base rectangle before the blur; shadow_opa is independent of bg_opa and
// resolves to LV_OPA_COVER when unset. No default is declared for spread or opa
// on purpose — unset stays unset, so nothing new is ever exported for a design
// that never touched them.
export const SHADOW_STYLE = {
    shadow_width: { type: 'int',    min: 0 },
    shadow_spread:{ type: 'int' },
    shadow_color: { type: 'color' },
    shadow_opa:   { type: 'int',    min: 0, max: 255 },
    shadow_ofs_x: { type: 'int',    label: 'shadow offset x' },
    shadow_ofs_y: { type: 'int',    label: 'shadow offset y' },
}

// The four optional per-side padding overrides that sit alongside a part's
// `padding` shorthand. Spread into a part that declares `padding` and whose
// Designer geometry treats it as a real 4-side box inset. Unlike `padding`
// (exportSpecial, 1 value → 4 wire keys) these are plain properties: on `main`
// their wire keys ARE pad_top/pad_right/pad_bottom/pad_left, so they flow
// through the generic export/import paths untouched. Effective-value fallback
// (side ?? padding ?? 0) lives in exactly one place — v2/padding.js.
export const PADDING_SIDES = {
    pad_top:    { type: 'int', min: 0, label: 'pad top' },
    pad_right:  { type: 'int', min: 0, label: 'pad right' },
    pad_bottom: { type: 'int', min: 0, label: 'pad bottom' },
    pad_left:   { type: 'int', min: 0, label: 'pad left' },
}

// LVGL 7 stores style integers in int16_t. Slider knob pads are real LVGL
// per-side values (and may be negative), unlike the older Designer shorthand
// padding properties used by most widgets.
export const LVGL_STYLE_INT_MIN = -32768
export const LVGL_STYLE_INT_MAX = 32767
export const SIGNED_PADDING_SIDES = {
    pad_top:    { type: 'int', min: LVGL_STYLE_INT_MIN, max: LVGL_STYLE_INT_MAX, label: 'pad top' },
    pad_right:  { type: 'int', min: LVGL_STYLE_INT_MIN, max: LVGL_STYLE_INT_MAX, label: 'pad right' },
    pad_bottom: { type: 'int', min: LVGL_STYLE_INT_MIN, max: LVGL_STYLE_INT_MAX, label: 'pad bottom' },
    pad_left:   { type: 'int', min: LVGL_STYLE_INT_MIN, max: LVGL_STYLE_INT_MAX, label: 'pad left' },
}

// Axis-level padding overrides, for a part whose widget can only distinguish
// HORIZONTAL from VERTICAL padding — top/bottom (and left/right) are not
// individually meaningful there, so exposing four sides would misrepresent it.
// Reference case: the checkbox bullet, whose box is sized per axis (see
// checkbox.js / HaspCheckbox.js's _bulletBox).
//
// `exportSpecial` because each of these expands to TWO wire keys — the same
// 1-to-many shape change `padding` has, handled in ObjectExport.js and folded
// back in ConfigManager.js. A part declares either PADDING_SIDES or
// PADDING_AXES, never both; the effective-value fallback for either lives in
// exactly one place — v2/padding.js.
export const PADDING_AXES = {
    pad_hor: { type: 'int', min: 0, label: 'pad horizontal', exportSpecial: true },
    pad_ver: { type: 'int', min: 0, label: 'pad vertical',   exportSpecial: true },
}

export const MAIN_STYLE = {
    bg_color:     { type: 'color',  format: 'hex8' },
    bg_opa:       { type: 'int',    min: 0, max: 255, internal: true },
    radius:       { type: 'int',    min: 0, max: 999 },
    ...BORDER_STYLE,
    ...SHADOW_STYLE,
    ...OUTLINE_STYLE,
}

// ---------------------------------------------------------------------------
// Property groups — OPTIONAL part-level Property Editor metadata.
//
// A part may declare `propertyGroups: [{ id, label, properties }]`. The
// Property Editor renders each declared group as its own accordion, in
// declaration order, and leaves everything ungrouped in the part's general
// accordion. A group whose properties this part doesn't actually declare (or
// declares only as `internal`) renders nothing at all, so the SAME shared group
// list can be handed to every widget — page, which has borders but no
// outline/shadow, simply shows one accordion.
//
// This is presentation metadata and nothing else: it never affects resolution,
// defaults, rendering, the wire format or export. Property ORDER inside a group
// is the group's own `properties` order (i.e. the style bundle's order).
//
// Groups are derived from the style bundles above rather than re-listing names,
// so a property added to a bundle lands in its accordion automatically.
// ---------------------------------------------------------------------------

// The overall shorthand plus its per-side overrides, rendered by the dedicated
// PaddingEditor (Overall + Top/Right/Bottom/Left) instead of five plain integer
// fields — see PropertiesPanel.vue's group loop. A part declaring only
// `padding` (no PADDING_SIDES) still gets the accordion, with Overall alone.
export const PADDING_GROUP = { id: 'padding', label: 'Padding', properties: ['padding', ...Object.keys(PADDING_SIDES)] }
export const BORDER_GROUP  = { id: 'border',  label: 'Border',  properties: Object.keys(BORDER_STYLE) }
export const OUTLINE_GROUP = { id: 'outline', label: 'Outline', properties: Object.keys(OUTLINE_STYLE) }
export const SHADOW_GROUP  = { id: 'shadow',  label: 'Shadow',  properties: Object.keys(SHADOW_STYLE) }

/**
 * The groups every `main` part gets. Spread into a part's `propertyGroups`
 * (`propertyGroups: [...MAIN_STYLE_GROUPS]`), optionally followed by that
 * widget's own groups — see gauge.js's Scale group.
 */
export const MAIN_STYLE_GROUPS = [PADDING_GROUP, BORDER_GROUP, OUTLINE_GROUP, SHADOW_GROUP]

export const INDICATOR_STYLE = {
    bg_color:     { type: 'color' },
    radius:       { type: 'int',  min: 0, max: 999 },
    border_width: { type: 'int',  min: 0 },
    border_color: { type: 'color' },
    line_color:   { type: 'color' },
    line_width:   { type: 'int',  min: 0 },
    line_rounded: { type: 'bool' },
    keep_circle:  { type: 'bool', internal: true },
}

export const KNOB_STYLE = {
    bg_color:     { type: 'color' },
    border_width: { type: 'int',  min: 0 },
    border_color: { type: 'color' },
    padding:      { type: 'int',  min: 0, exportSpecial: true },
    keep_circle:  { type: 'bool', internal: true },
}

// Bar and Slider use the same LVGL BG/INDIC image-source attributes. Keep the
// schema metadata shared so the two widgets cannot drift apart; Slider adds
// its separate KNOB image source in slider.js.
export const BAR_IMAGE_SOURCE_STYLE = {
    img_src: { type: 'resource', resourceType: 'image', label: 'Image source' },
}

// Bar and Slider share their value/range semantics. Keep step here so both
// schemas expose the same property and default without duplicating metadata.
export const BAR_SLIDER_VALUE_PROPERTIES = {
    step: { type: 'float', min: 0, label: 'Step' },
}

// Shared appearance for a chart's scale/axis (label text + tick marks).
// Reused verbatim by chart.js's `x_axis` and `y_axis` parts — do not
// duplicate these properties per-axis, add to this set instead.
//
// The Chart widget is a custom extension of the user's own OpenHASP fork —
// per that standing direction, the Designer is the reference model for
// Chart, not current upstream firmware. Every property here is fully
// implemented in the Designer (rendered by HaspChart.js's drawChart(),
// exported generically like any other part property, no widget-specific
// code required) even where current firmware has no matching attribute yet
// — that's expected under this model, not a gap to apologize for. Firmware
// is expected to catch up to this shape later, not the other way around.
export const AXIS_STYLE = {
    text_font:        { type: 'int',    min: 0, max: 48 },
    text_font_family: { type: 'string', internal: true },
    font:             { type: 'resource', resourceType: 'font', label: 'Font Family', inline: true },
    text_color:       { type: 'color' },

    // Major ticks — the labeled tick marks (one per grid division on this
    // axis; see HaspChart.js's drawChart(), which today renders ALL of an
    // axis's ticks as "major"). LVGL genuinely distinguishes major vs minor
    // ticks (lv_chart_set_x/y_tick_length()'s separate major_tick_len/
    // minor_tick_len params) — modeled here as two independent property
    // groups so the Designer's domain model already has room for minor
    // ticks once they're implemented, without another schema migration.
    major_tick_color: { type: 'color' },
    major_tick_width: { type: 'int',   min: 0 },
    // Tick mark length in px; 0 = auto, re-computed from the chart's current
    // size on every redraw (matches LVGL's LV_CHART_TICK_LENGTH_AUTO
    // behavior) — still available as an explicit override to opt back into
    // per-resize auto-scaling. The default (see chart.js) is a fixed API
    // constant, identical for x_axis and y_axis — a property of the chart's
    // default STYLE, not of chart size or axis orientation — rather than a
    // number derived from this schema's default width/height. Firmware has
    // no per-axis tick-length attribute yet; the Designer models it ahead of
    // that support.
    major_tick_size:  { type: 'int',   min: 0 },

    // Minor ticks — unlabeled subdivisions drawn between each pair of
    // adjacent major ticks (see HaspChart.js's drawChart()). minor_tick_size
    // defaults to visibly smaller than major_tick_size (see chart.js) so
    // minor ticks read as subdivisions, not a second set of major ticks.
    minor_tick_color: { type: 'color' },
    minor_tick_width: { type: 'int',   min: 0 },
    minor_tick_size:  { type: 'int',   min: 0 },
    // Number of equal intervals a major-to-major gap is subdivided into —
    // NOT the number of minor ticks drawn. E.g. minor_tick_count: 4 divides
    // the gap into 4 equal parts, placing 3 minor ticks strictly between the
    // two major ticks (at 1/4, 2/4, 3/4) — the majors themselves are never
    // counted or redrawn here. 0 or 1 means no subdivision (no minor ticks
    // drawn for that axis), matching "1 interval = the gap itself, nothing
    // in between".
    minor_tick_count: { type: 'int',   min: 0 },

    // Layout — distance in px between the end of a MAJOR tick and the
    // nearest edge of its label (see HaspChart.js's drawChart()). A distinct
    // property from the widget's own top-level `padding` (the uniform
    // border inset around the whole plot area) — this is purely the
    // tick-to-label gap on this specific axis, one of the few axis-layout
    // knobs LVGL genuinely exposes independently per axis (SERIES_BG's
    // pad_left for Y / pad_bottom for X, read as `label_dist`/`label_gap` in
    // hasp_chart.cpp's hasp_chart_set_y_scale/_x_scale — see the LVGL axis
    // capability analysis). Feeds both label positioning AND the automatic
    // padding calculation, so widening this axis's gap grows the reserved
    // space for it automatically — no manual widget padding change needed.
    label_gap: { type: 'int', min: 0 },
}

// ---------------------------------------------------------------------------
// Common properties shared by every real LVGL object.
// Injected automatically via wireFormat helpers — no need to repeat in each
// object file.  Individual schemas can override by redefining the same key.
// ---------------------------------------------------------------------------

/** Metadata for properties that apply to all objects. */
export const COMMON_PROPS = {
    opacity:     { type: 'int',  min: 0, max: 255 },
    click:       { type: 'bool' },
    ext_click_h: { type: 'int',  min: 0, label: 'ext click h' },
    ext_click_v: { type: 'int',  min: 0, label: 'ext click v' },
}

/** Firmware-default values for common properties (used in node.defaults). */
export const COMMON_DEFAULTS = {
    opacity:     255,
    click:       true,
    ext_click_h: 0,
    ext_click_v: 0,
    // bg_opa: 0 = transparent background by default — matches firmware behaviour.
    // Explicitly listed here so the bg_color handler in ConfigManager always has
    // a numeric value to work with even when bg_opa is absent from the JSON.
    bg_opa:      0,
}
